import {
  COMPANY_ADDRESS_FULL,
  COMPANY_INITIALS,
  COMPANY_LOCATION,
  COMPANY_NAME,
  COMPANY_REGION,
  COMPANY_SHORT,
  COMPANY_TAGLINE,
  HOUSE_NOTES,
  MAP_URL,
  MARQUEE_WORDS,
  PROFILE_FACTS,
  PUBLIC_INFO,
} from './constants';

function WaveBackdropDark() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 1440 720"
      className="pointer-events-none absolute inset-0 h-full w-full"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="deepGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0A2A33" />
          <stop offset="55%" stopColor="#0E4F5E" />
          <stop offset="100%" stopColor="#0A2A33" />
        </linearGradient>
        <radialGradient id="moon" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#EFE6D3" stopOpacity="0.9" />
          <stop offset="60%" stopColor="#EFE6D3" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#EFE6D3" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="1440" height="720" fill="url(#deepGrad)" />
      <g fill="none" stroke="#EFE6D3" strokeOpacity="0.12" strokeWidth="1">
        <path d="M0,500 C220,550 420,440 720,500 C1020,560 1220,440 1440,500" />
        <path d="M0,540 C240,590 460,480 760,540 C1060,600 1240,480 1440,540" />
        <path d="M0,580 C260,630 500,520 800,580 C1100,640 1260,520 1440,580" />
        <path d="M0,620 C280,670 540,560 840,620 C1140,680 1280,560 1440,620" />
        <path d="M0,660 C300,710 580,600 880,660 C1180,720 1300,600 1440,660" />
      </g>
      <circle cx="1180" cy="170" r="220" fill="url(#moon)" />
      <circle cx="1180" cy="170" r="64" fill="#FAF5EC" fillOpacity="0.92" />
      <circle cx="1206" cy="150" r="64" fill="#0E4F5E" />
    </svg>
  );
}

function TopBar() {
  const navItems = [
    { label: 'House', href: '#house' },
    { label: 'Notes', href: '#notes' },
    { label: 'Dossier', href: '#dossier' },
    { label: 'Address', href: '#address' },
  ];

  return (
    <header className="sticky top-0 z-30 border-b border-bone/10 bg-deep/85 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-4">
        <a href="#top" className="flex min-w-0 items-center gap-3" aria-label={`${COMPANY_SHORT} home`}>
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-coral/40 bg-coral/15 font-display text-xs font-black tracking-wide text-coral">
            {COMPANY_INITIALS}
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-display text-base font-black tracking-tight text-bone sm:text-lg">
              {COMPANY_SHORT}
            </span>
            <span className="text-[10px] font-bold uppercase tracking-widest2 text-bone/55">
              {COMPANY_REGION}
            </span>
          </span>
        </a>
        <nav
          className="hidden items-center gap-8 text-[13px] font-semibold uppercase tracking-widish text-bone/70 md:flex"
          aria-label="Primary"
        >
          {navItems.map((item) => (
            <a key={item.href} href={item.href} className="transition hover:text-coral">
              {item.label}
            </a>
          ))}
        </nav>
        <a
          href="#address"
          className="rounded-full border border-bone/25 px-4 py-2 text-[12px] font-bold uppercase tracking-widish text-bone transition hover:border-coral hover:bg-coral hover:text-deep"
        >
          Find the House
        </a>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section id="house" className="relative overflow-hidden border-b border-bone/10 text-bone grain">
      <WaveBackdropDark />
      <div className="relative mx-auto grid max-w-7xl gap-12 px-6 pt-20 pb-24 lg:grid-cols-12 lg:pt-28 lg:pb-32">
        <div className="lg:col-span-7 lg:pr-8">
          <p className="flex items-center gap-3 text-[11px] font-black uppercase tracking-widest2 text-coral">
            <span className="inline-block h-2 w-2 rounded-full bg-coral" />
            Public profile · {COMPANY_LOCATION}
          </p>
          <h1 className="mt-8 font-display text-[44px] font-black leading-[1.02] tracking-tight text-bone sm:text-6xl lg:text-7xl">
            {COMPANY_NAME.split(' ').map((word, i) => (
              <span
                key={`${word}-${i}`}
                className={i === 1 ? 'block italic text-coral' : 'block'}
              >
                {word}
              </span>
            ))}
          </h1>
          <p className="mt-8 max-w-xl text-lg leading-8 text-bone/75">
            {COMPANY_TAGLINE} This page is the editorial, public-safe profile — just the
            name, the city, and the address. Nothing operational, nothing private.
          </p>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <a
              href="#notes"
              className="rounded-full bg-coral px-6 py-3 text-center text-sm font-bold uppercase tracking-widish text-deep transition hover:bg-ember hover:text-bone"
            >
              Read House Notes
            </a>
            <a
              href="#address"
              className="rounded-full border border-bone/30 px-6 py-3 text-center text-sm font-bold uppercase tracking-widish text-bone transition hover:border-coral hover:text-coral"
            >
              Address & Map
            </a>
          </div>
        </div>

        <aside className="relative lg:col-span-5">
          <div className="relative ml-auto flex h-full max-w-sm flex-col justify-between rounded-3xl border border-bone/15 bg-bone/5 p-7 shadow-soft backdrop-blur">
            <div>
              <p className="text-[11px] font-black uppercase tracking-widest2 text-coral">
                House Card
              </p>
              <p className="mt-5 font-display text-2xl font-black leading-snug text-bone">
                {COMPANY_SHORT}
              </p>
              <p className="mt-1 text-sm text-bone/60">{COMPANY_REGION}</p>
            </div>
            <div className="mt-10 rounded-2xl border border-coral/30 bg-coral/10 p-5">
              <p className="text-[10px] font-black uppercase tracking-widest2 text-coral">
                Street Address
              </p>
              <p className="mt-3 font-display text-lg font-black leading-snug text-bone">
                {COMPANY_ADDRESS_FULL}
              </p>
            </div>
            <div className="mt-6 flex items-center justify-between text-[11px] font-bold uppercase tracking-widest2 text-bone/50">
              <span>Lake Erie</span>
              <span className="h-px flex-1 bg-bone/20 mx-3" />
              <span>West Side</span>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}

function Marquee() {
  const items = [...MARQUEE_WORDS, ...MARQUEE_WORDS];
  return (
    <section aria-hidden="true" className="overflow-hidden border-y border-bone/10 bg-slate text-bone">
      <div className="flex w-max marquee-track gap-12 px-6 py-4 font-display text-2xl font-black tracking-tight sm:text-3xl">
        {items.map((word, i) => (
          <span key={`${word}-${i}`} className="flex shrink-0 items-center gap-12">
            <span className={i % 3 === 1 ? 'italic text-coral' : 'text-bone'}>{word}</span>
            <span aria-hidden="true" className="text-coral">◦</span>
          </span>
        ))}
      </div>
    </section>
  );
}

function FactsStrip() {
  return (
    <section aria-label="Profile facts" className="bg-deep text-bone">
      <div className="mx-auto grid max-w-7xl gap-px bg-bone/10 sm:grid-cols-2 lg:grid-cols-4">
        {PROFILE_FACTS.map((fact, i) => (
          <div key={fact.label} className="bg-deep px-6 py-10">
            <p className="font-mono text-[11px] font-bold uppercase tracking-widest2 text-coral">
              {String(i + 1).padStart(2, '0')} · {fact.label}
            </p>
            <p className="mt-3 font-display text-2xl font-black leading-tight text-bone">
              {fact.value}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function HouseNotes() {
  return (
    <section id="notes" className="bg-slate text-bone">
      <div className="mx-auto max-w-7xl px-6 py-20 lg:py-24">
        <div className="flex flex-col gap-6 border-b border-bone/15 pb-10 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-xl">
            <p className="text-[11px] font-black uppercase tracking-widest2 text-coral">
              House Notes
            </p>
            <h2 className="mt-4 font-display text-4xl font-black leading-tight sm:text-5xl">
              Four short notes <span className="italic text-coral">about the place.</span>
            </h2>
          </div>
          <p className="max-w-xs text-[11px] font-bold uppercase tracking-widest2 text-bone/55">
            Lake light · long lawns · slow streets · public-safe profile only.
          </p>
        </div>
        <ol className="mt-12 grid gap-px bg-bone/10 sm:grid-cols-2">
          {HOUSE_NOTES.map((note) => (
            <li
              key={note.number}
              className="flex flex-col gap-4 bg-slate p-8 transition hover:bg-deep"
            >
              <div className="flex items-baseline justify-between">
                <span className="font-display text-5xl font-black text-coral">{note.number}</span>
                <span className="h-px w-16 bg-bone/30" />
              </div>
              <h3 className="font-display text-2xl font-black leading-snug text-bone">
                {note.title}
              </h3>
              <p className="text-sm leading-7 text-bone/70">{note.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function HouseDossier() {
  return (
    <section id="dossier" className="relative bg-sand text-slate grain-light">
      <div className="relative mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-12 lg:py-24">
        <div className="lg:col-span-5">
          <p className="text-[11px] font-black uppercase tracking-widest2 text-ember">
            Dossier
          </p>
          <h2 className="mt-4 font-display text-4xl font-black leading-tight text-slate sm:text-5xl">
            One house. <span className="italic text-lake">One address.</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-7 text-slate/75">
            The public dossier is intentionally short. It names the house and points to where
            on the West Side it sits — and stops there. Anything else is left to the people
            who actually open the door.
          </p>
          <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-slate/20 px-4 py-2 text-[11px] font-bold uppercase tracking-widest2 text-slate/70">
            <span className="inline-block h-2 w-2 rounded-full bg-coral" />
            Single editorial moment
          </div>
        </div>
        <div className="grid gap-5 lg:col-span-7 lg:grid-cols-2">
          <article className="rounded-3xl border border-slate/15 bg-bone p-7">
            <p className="font-mono text-[11px] font-bold uppercase tracking-widest2 text-lake">
              Identity
            </p>
            <p className="mt-4 font-display text-2xl font-black leading-snug text-slate">
              {COMPANY_NAME}
            </p>
            <p className="mt-3 text-sm leading-6 text-slate/65">
              A house name first, a place second. Treat the wordmark as the entry point.
            </p>
          </article>
          <article className="rounded-3xl border border-coral/40 bg-coral/15 p-7">
            <p className="font-mono text-[11px] font-bold uppercase tracking-widest2 text-ember">
              Footprint
            </p>
            <p className="mt-4 font-display text-2xl font-black leading-snug text-slate">
              {COMPANY_REGION}
            </p>
            <p className="mt-3 text-sm leading-6 text-slate/75">
              Cleveland, west of the river, in the 44111 zip. Lake-adjacent without being on
              the water.
            </p>
          </article>
          <article className="rounded-3xl border border-slate/15 bg-bone/80 p-7 sm:col-span-2">
            <p className="font-mono text-[11px] font-bold uppercase tracking-widest2 text-lake">
              Profile Note
            </p>
            <p className="mt-4 text-base leading-7 text-slate/80">
              This page keeps the source-backed details visible — the name, the city, the
              street — and leaves private registry and tax identifiers off the page on
              purpose.
            </p>
          </article>
        </div>
      </div>
    </section>
  );
}

function AddressBand() {
  return (
    <section id="address" className="bg-deep text-bone">
      <div className="mx-auto grid max-w-7xl gap-10 px-6 py-20 lg:grid-cols-12 lg:items-stretch lg:py-24">
        <div className="lg:col-span-5">
          <p className="text-[11px] font-black uppercase tracking-widest2 text-coral">
            Address
          </p>
          <h2 className="mt-4 font-display text-4xl font-black leading-tight sm:text-5xl">
            {COMPANY_LOCATION}
          </h2>
          <p className="mt-5 max-w-md text-base leading-7 text-bone/70">
            Use the street address below for directory, public-profile, and correspondence
            reference. There is nothing else on this page that needs to be private.
          </p>
          <div className="mt-8 grid gap-3">
            {PUBLIC_INFO.map((item) => (
              <div
                key={item.label}
                className="flex items-center justify-between border-b border-bone/15 pb-3 text-sm"
              >
                <span className="font-mono text-[11px] font-bold uppercase tracking-widest2 text-bone/55">
                  {item.label}
                </span>
                <span className="font-display text-base font-black text-bone">{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative overflow-hidden rounded-3xl border border-bone/15 bg-slate p-8 text-bone shadow-soft lg:col-span-7">
          <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-coral/20" />
          <div className="pointer-events-none absolute -bottom-32 -left-16 h-72 w-72 rounded-full bg-lake/25" />
          <p className="relative font-mono text-[11px] font-bold uppercase tracking-widest2 text-coral">
            House Address
          </p>
          <p className="relative mt-6 font-display text-3xl font-black leading-tight text-bone sm:text-4xl">
            {COMPANY_ADDRESS_FULL}
          </p>
          <p className="relative mt-3 text-sm text-bone/55">{COMPANY_REGION}</p>
          <div className="relative mt-10 flex flex-col gap-3 sm:flex-row">
            <a
              href={MAP_URL}
              target="_blank"
              rel="noreferrer"
              className="rounded-full bg-coral px-6 py-3 text-center text-sm font-bold uppercase tracking-widish text-deep transition hover:bg-ember hover:text-bone"
            >
              Open in Google Maps
            </a>
            <a
              href="#top"
              className="rounded-full border border-bone/30 px-6 py-3 text-center text-sm font-bold uppercase tracking-widish text-bone transition hover:border-coral hover:text-coral"
            >
              Back to Top
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-bone/10 bg-deep text-bone/75">
      <div className="mx-auto flex max-w-7xl flex-col gap-5 px-6 py-10 text-sm md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-full border border-coral/40 bg-coral/15 font-display text-xs font-black tracking-wide text-coral">
            {COMPANY_INITIALS}
          </span>
          <p className="font-display text-base font-black text-bone">
            &copy; {new Date().getFullYear()} {COMPANY_NAME}
          </p>
        </div>
        <p className="font-mono text-[11px] font-bold uppercase tracking-widest2 text-bone/55">
          {COMPANY_LOCATION} · {COMPANY_REGION}
        </p>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div id="top" className="min-h-screen bg-deep text-bone">
      <a
        href="#house"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:bg-coral focus:px-3 focus:py-2 focus:text-deep"
      >
        Skip to content
      </a>
      <TopBar />
      <main>
        <Hero />
        <Marquee />
        <FactsStrip />
        <HouseNotes />
        <HouseDossier />
        <AddressBand />
      </main>
      <Footer />
    </div>
  );
}

export default App;
